import React, { useState, useEffect, useRef } from 'react';
import { Code2, Search, ChevronDown, ChevronRight } from 'lucide-react';
import apiClient from '../../../../services/api-client';

// ── Variable definitions ────────────────────────────────────────────────────

interface VariableItem {
  variable: string;
  key: string;
  label: string;
  description: string;
}

interface VariableGroup {
  group: string;
  icon: string;
  items: VariableItem[];
}

const STATIC_GROUPS: VariableGroup[] = [
  {
    group: 'Ticket',
    icon: '🎫',
    items: [
      { variable: '{{entity.id}}', key: 'ticketId', label: 'Ticket ID', description: 'Unique identifier (UUID)' },
      { variable: '{{entity.ticketNumber}}', key: 'ticketNumber', label: 'Ticket Number', description: 'Human-readable number' },
      { variable: '{{entity.subject}}', key: 'subject', label: 'Subject', description: 'Ticket subject line' },
      { variable: '{{entity.description}}', key: 'description', label: 'Description', description: 'Full ticket body' },
      { variable: '{{entity.status}}', key: 'status', label: 'Status', description: 'open, in_progress, resolved, closed' },
      { variable: '{{entity.priority}}', key: 'priority', label: 'Priority', description: 'low, medium, high, urgent' },
      { variable: '{{entity.channel}}', key: 'channel', label: 'Channel', description: 'email, chat, call, web' },
      { variable: '{{entity.tags}}', key: 'tags', label: 'Tags', description: 'Tag list' },
      { variable: '{{entity.createdAt}}', key: 'createdAt', label: 'Created At', description: 'Creation timestamp' },
    ],
  },
  {
    group: 'Contact',
    icon: '👤',
    items: [
      { variable: '{{entity.contactName}}', key: 'contactName', label: 'Contact Name', description: 'Customer name' },
      { variable: '{{entity.contactEmail}}', key: 'contactEmail', label: 'Contact Email', description: 'Customer email' },
      { variable: '{{entity.customerId}}', key: 'customerId', label: 'Customer ID', description: 'Customer UUID' },
    ],
  },
  {
    group: 'Assignment',
    icon: '👥',
    items: [
      { variable: '{{entity.assigneeId}}', key: 'assigneeId', label: 'Assignee ID', description: 'Assigned agent' },
      { variable: '{{entity.teamId}}', key: 'teamId', label: 'Team ID', description: 'Assigned team' },
    ],
  },
  {
    group: 'AI Classification',
    icon: '🤖',
    items: [
      { variable: '{{entity.ticketTypeId}}', key: 'ticketTypeId', label: 'Ticket Type ID', description: 'Classified type UUID' },
      { variable: '{{entity.ticketTypeName}}', key: 'ticketTypeName', label: 'Ticket Type Name', description: 'e.g. Quote, Refund' },
      { variable: '{{entity.categoryId}}', key: 'categoryId', label: 'Category ID', description: 'Category UUID' },
      { variable: '{{entity.aiClassification.confidence}}', key: 'confidence', label: 'AI Confidence', description: '0 to 1 score' },
      { variable: '{{entity.aiClassification.method}}', key: 'classificationMethod', label: 'AI Method', description: 'llm or keyword_fallback' },
    ],
  },
  {
    group: 'Pipeline',
    icon: '📊',
    items: [
      { variable: '{{entity.pipelineId}}', key: 'pipelineId', label: 'Pipeline ID', description: 'Pipeline identifier' },
      { variable: '{{entity.stageId}}', key: 'stageId', label: 'Stage ID', description: 'Current stage' },
    ],
  },
  {
    group: 'System',
    icon: '⚙️',
    items: [
      { variable: '{{tenantId}}', key: 'tenantId', label: 'Tenant ID', description: 'Organisation ID' },
    ],
  },
];

// ── Hook to fetch custom fields dynamically ─────────────────────────────────

function useCustomFieldVariables(): VariableGroup | null {
  const [group, setGroup] = useState<VariableGroup | null>(null);

  useEffect(() => {
    apiClient
      .get('/field-definitions?isActive=true')
      .then((res) => {
        const data = res.data;
        const fields = Array.isArray(data) ? data : data?.data ?? [];
        if (!fields.length) return;

        const items: VariableItem[] = fields
          .filter((f: any) => f.isActive)
          .map((f: any) => ({
            variable: `{{entity.customFields.${f.key}}}`,
            key: f.key,
            label: f.name,
            description: f.description || `Custom ${f.fieldType || f.type} field`,
          }));

        if (items.length) {
          setGroup({ group: 'Custom Fields', icon: '🏷️', items });
        }
      })
      .catch(() => {});
  }, []);

  return group;
}

// ── Smart JSON insertion helper ─────────────────────────────────────────────

function insertIntoJsonBody(currentValue: string, item: VariableItem): string {
  const trimmed = currentValue.trim();

  if (!trimmed) {
    return `{\n  "${item.key}": "${item.variable}"\n}`;
  }

  if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
    const inner = trimmed.slice(1, -1).trimEnd();
    const needsComma = inner.trim().length > 0 && !inner.trimEnd().endsWith(',');
    const comma = needsComma ? ',' : '';
    const newEntry = `  "${item.key}": "${item.variable}"`;
    return `${trimmed.slice(0, -1).trimEnd()}${comma}\n${newEntry}\n}`;
  }

  return currentValue + item.variable;
}

// ── VariablePicker component ────────────────────────────────────────────────

interface VariablePickerProps {
  textareaRef: React.RefObject<HTMLTextAreaElement | HTMLInputElement>;
  onInsert: (newValue: string) => void;
  jsonMode?: boolean;
}

export function VariablePicker({ textareaRef, onInsert, jsonMode = false }: VariablePickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const pickerRef = useRef<HTMLDivElement>(null);

  const customFieldGroup = useCustomFieldVariables();

  const allGroups: VariableGroup[] = [
    ...STATIC_GROUPS,
    ...(customFieldGroup ? [customFieldGroup] : []),
  ];

  const filteredGroups = allGroups
    .map((g) => ({
      ...g,
      items: g.items.filter(
        (item) =>
          item.label.toLowerCase().includes(search.toLowerCase()) ||
          item.variable.toLowerCase().includes(search.toLowerCase()) ||
          item.description.toLowerCase().includes(search.toLowerCase()),
      ),
    }))
    .filter((g) => g.items.length > 0);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleSelect = (item: VariableItem) => {
    const el = textareaRef.current;

    if (jsonMode) {
      const currentVal = el?.value || '';
      const newValue = insertIntoJsonBody(currentVal, item);
      onInsert(newValue);
    } else {
      if (!el) {
        onInsert(item.variable);
      } else {
        const start = el.selectionStart ?? el.value.length;
        const end = el.selectionEnd ?? el.value.length;
        const newValue = el.value.substring(0, start) + item.variable + el.value.substring(end);
        onInsert(newValue);

        requestAnimationFrame(() => {
          el.focus();
          const cursorPos = start + item.variable.length;
          el.setSelectionRange(cursorPos, cursorPos);
        });
      }
    }

    setOpen(false);
  };

  const toggleGroup = (group: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  return (
    <div className="relative inline-block" ref={pickerRef}>
      <button
        type="button"
        onClick={() => { setOpen(!open); setSearch(''); }}
        className="flex items-center gap-1 rounded border border-dashed border-primary-400 bg-primary-50 px-1.5 py-0.5 text-[10px] font-medium text-primary-600 hover:bg-primary-100 dark:border-primary-500 dark:bg-primary-900/20 dark:text-primary-400"
        title="Insert dynamic variable"
      >
        <Code2 size={10} />
        {jsonMode ? 'Add Field' : 'Variable'}
      </button>

      {open && (
        <div
          className="absolute z-[100] mt-1 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl dark:border-gray-600 dark:bg-gray-800"
          style={{ left: 0, width: '260px' }}
        >
          {/* Search */}
          <div className="border-b border-gray-100 p-2 dark:border-gray-700">
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder="Search variables…"
                className="w-full rounded border border-gray-200 bg-gray-50 py-1 pl-7 pr-2 text-[11px] focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </div>
          </div>

          {/* Variable list */}
          <div className="overflow-y-auto" style={{ maxHeight: '280px' }}>
            {filteredGroups.length === 0 && (
              <p className="px-3 py-4 text-center text-[11px] text-gray-400 dark:text-gray-500">No variables found</p>
            )}

            {filteredGroups.map((group) => (
              <div key={group.group}>
                {/* Group header */}
                <button
                  type="button"
                  onClick={() => toggleGroup(group.group)}
                  className="flex w-full items-center gap-1.5 bg-gray-50 px-2.5 py-1.5 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500 hover:bg-gray-100 dark:bg-gray-750 dark:text-gray-400 dark:hover:bg-gray-700"
                >
                  {collapsed.has(group.group) ? <ChevronRight size={10} /> : <ChevronDown size={10} />}
                  <span>{group.icon} {group.group}</span>
                  <span className="ml-auto text-[9px] font-normal text-gray-400 dark:text-gray-500">{group.items.length}</span>
                </button>

                {/* Items — vertical stack layout */}
                {!collapsed.has(group.group) && (
                  <div>
                    {group.items.map((item) => (
                      <button
                        key={item.variable}
                        type="button"
                        onClick={() => handleSelect(item)}
                        className="group block w-full border-b border-gray-50 px-3 py-2 text-left transition-colors hover:bg-primary-50 dark:border-gray-700/30 dark:hover:bg-primary-900/20"
                      >
                        {/* Row 1: Label */}
                        <div className="text-[11px] font-semibold text-gray-800 group-hover:text-primary-700 dark:text-gray-200">
                          {item.label}
                        </div>
                        {/* Row 2: Variable code */}
                        <code className="mt-0.5 inline-block rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500 group-hover:bg-primary-100 group-hover:text-primary-600 dark:bg-gray-700 dark:text-gray-400">
                          {item.variable}
                        </code>
                        {/* Row 3: Description */}
                        <div className="mt-0.5 text-[10px] text-gray-400 dark:text-gray-500">
                          {item.description}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-100 bg-gray-50 px-2.5 py-1.5 dark:border-gray-700 dark:bg-gray-750">
            <p className="text-[9px] text-gray-400 dark:text-gray-500">
              {jsonMode ? 'Click to add "key": "{{var}}" to JSON' : 'Click to insert at cursor'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
